/**
 * 拼"前台地址"的公共逻辑。
 *
 * 控制台里有两处需要打开客户站点：内容页的预览（带令牌）和商品列表的直达链接。
 * 两者都要回答同一个问题——这个 host 该用 http 还是 https，所以放在一处，
 * 免得哪天本地调试的规则改了只改对一半。
 */

import type { ProductStatus } from '@icetus/contracts';

/**
 * 前台地址的协议。
 *
 * 生产站点全部是 https（Cloudflare 强制），只有本地调试的 localhost / *.local
 * 走 http。写死 https 的话，本地点的链接会打开一个 443 上根本不存在的服务，
 * 而错误页是浏览器的、和我们的代码毫无关系，排查时最容易被带偏。
 *
 * 判本地之前先去掉端口：本地几乎总是带端口（`localhost:3000`、
 * `schoolmemo.localhost:3000`），拿带端口的字符串去比 `localhost`
 * 永远比不中，结果就是"本地也一律 https"——这个坑原来藏在内容页的
 * 预览里，2026-10-29 加测试时才发现。
 */
export function siteOrigin(host: string): string {
  const bare = host.replace(/:\d+$/, '');
  const local =
    bare === 'localhost' ||
    bare === '[::1]' ||
    bare.startsWith('127.') ||
    bare.endsWith('.localhost') ||
    bare.endsWith('.local');
  return `${local ? 'http' : 'https'}://${host}`;
}

/**
 * 商品在前台的规范地址。
 *
 * 用的是**默认语言**的地址（不带语言前缀），因为列表页不区分语言，
 * 而 `ProductSummary.slug` 就是 products.slug 本身，即默认语言的生效值
 * （非默认语言的覆盖值只存在于 ProductDetail.translations 里）。
 *
 * 站点没绑域名时返回 null：`https://undefined/products/x` 这种地址
 * 点开只会是一片空白，比按钮不可用更让人困惑。
 */
export function frontProductUrl(host: string | null | undefined, slug: string): string | null {
  if (!host || !slug) return null;
  return `${siteOrigin(host)}/products/${slug}`;
}

/**
 * 这个商品在前台能不能打开、打不开是因为什么。
 *
 * 两种"点了没内容"要分开说，因为处理方式完全不同：
 * - **站点没绑域名**是站点级问题，得去「域名」页配一个；
 * - **草稿/归档**是商品自己的状态，前台详情查询里写死了 `status = 'active'`，
 *   所以哪怕是预览态也打不开（预览令牌只管页面草稿，不管商品）。
 *
 * 草稿一律不给地址：给出一个必然 404 的链接，比按钮灰着更让人困惑 ——
 * 客户会以为是站点坏了，而不是"这东西还没上架"。
 */
export function frontTarget(
  host: string,
  item: { slug: string; status: ProductStatus },
): { href: string | null; hint: string } {
  if (item.status !== 'active') {
    return {
      href: null,
      hint:
        item.status === 'archived'
          ? '已归档的商品不在前台显示。改回「已上架」后即可打开。'
          : '草稿不会出现在前台，上架后即可打开。',
    };
  }
  const href = frontProductUrl(host, item.slug);
  return {
    href,
    hint: href ? '' : '这个站点还没有绑定域名，前台打不开。先到「域名」页绑一个。',
  };
}
